(() => {
/* Wild Gainz UGC + reviews carousel.
   Progressive enhancement: without this script every testimonial renders
   in a scrollable row, the arrows stay hidden, and the video controls stay
   hidden. With it, three testimonials show at a time and videos autoplay
   muted for at most the configured number at once. Each video gets a
   play/pause toggle that resumes where it left off, and a sound toggle
   that unmutes one video at a time; muting again also pauses so nobody
   loses their place. Reduced motion pauses everything. */

class WildgainzUgcReviews extends HTMLElement {
  connectedCallback() {
    if (this.bound) return;
    this.bound = true;

    this.cards = Array.from(this.querySelectorAll('[data-testimonial]'));
    this.prevButton = this.querySelector('[data-carousel-prev]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.videos = Array.from(this.querySelectorAll('.wg-ugc-card__video'));
    this.visibleCount = 3;
    this.index = 0;
    this.maxPlaying = parseInt(this.dataset.maxPlaying, 10) || 2;
    this.inView = new Set();
    this.userPaused = new Set();
    this.userStarted = new Set();
    this.audible = null;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onMotionChange = () => this.syncPlayback();
    this.reducedMotion.addEventListener('change', this.onMotionChange);

    if ('IntersectionObserver' in window && this.videos.length) {
      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.inView.add(entry.target);
            } else {
              this.inView.delete(entry.target);
              this.userStarted.delete(entry.target);
              // Sound does not follow the visitor down the page.
              if (this.audible === entry.target) this.setAudible(null);
            }
          });
          this.syncPlayback();
        },
        { threshold: 0.25 }
      );
      this.videos.forEach((video) => this.videoObserver.observe(video));
    }

    this.videos.forEach((video) => {
      const controls = this.controlsFor(video);
      if (!controls) return;
      controls.wrap.removeAttribute('hidden');

      // The buttons mirror the element's real state, so every pause path,
      // including the at-once cap and scrolling away, updates the icon.
      video.addEventListener('play', () => this.updatePlayButton(video));
      video.addEventListener('pause', () => this.updatePlayButton(video));
      this.updatePlayButton(video);

      if (controls.playPause) {
        controls.playPause.addEventListener('click', () => {
          if (video.paused) {
            this.userPaused.delete(video);
            this.userStarted.add(video);
          } else {
            this.userPaused.add(video);
            this.userStarted.delete(video);
          }
          this.syncPlayback();
        });
      }

      const toggleSound = () => {
        if (this.audible === video) {
          // Just mute; pausing is the pause button's job.
          this.setAudible(null);
        } else {
          this.setAudible(video);
          this.userPaused.delete(video);
          this.userStarted.add(video);
        }
        this.syncPlayback();
      };

      if (controls.sound) controls.sound.addEventListener('click', toggleSound);

      // Tapping the video itself toggles sound, feed-app style, with a
      // center burst as feedback. Buttons sit above and keep their clicks.
      video.addEventListener('click', () => {
        toggleSound();
        this.flash(video, this.audible === video);
      });

      // Playback progress line along the card's bottom edge.
      const media = video.closest('.wg-ugc-card__media');
      const progressBar = media ? media.querySelector('[data-ugc-progress-bar]') : null;
      if (progressBar) {
        video.addEventListener('timeupdate', () => {
          if (video.duration > 0) {
            progressBar.style.transform = 'scaleX(' + video.currentTime / video.duration + ')';
          }
        });
      }
    });

    if (this.cards.length > 1) {
      this.setAttribute('data-enhanced', '');
      if (this.prevButton) this.prevButton.addEventListener('click', () => this.step(-1));
      if (this.nextButton) this.nextButton.addEventListener('click', () => this.step(1));
      this.update();
    }

    if (window.Shopify && window.Shopify.designMode) {
      this.onBlockSelect = (event) => {
        const testimonial = event.target.closest ? event.target.closest('[data-testimonial]') : null;
        if (testimonial && this.contains(testimonial)) {
          this.index = this.cards.indexOf(testimonial);
          if (this.hasAttribute('data-enhanced')) this.update();
          return;
        }
        const ugcCard = event.target.closest ? event.target.closest('.wg-ugc-card') : null;
        if (ugcCard && this.contains(ugcCard)) {
          ugcCard.scrollIntoView({
            behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
            block: 'nearest',
            inline: 'center',
          });
        }
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
    }
  }

  disconnectedCallback() {
    this.reducedMotion.removeEventListener('change', this.onMotionChange);
    if (this.videoObserver) this.videoObserver.disconnect();
    if (this.onBlockSelect) {
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }
  }

  controlsFor(video) {
    const media = video.closest('.wg-ugc-card__media');
    if (!media) return null;
    const wrap = media.querySelector('[data-ugc-controls]');
    if (!wrap) return null;
    return {
      wrap: wrap,
      playPause: wrap.querySelector('[data-ugc-playpause]'),
      sound: wrap.querySelector('[data-ugc-sound]'),
    };
  }

  /* Center icon burst on video taps, skipped for reduced motion. */
  flash(video, soundOn) {
    if (this.reducedMotion.matches) return;
    const media = video.closest('.wg-ugc-card__media');
    const flash = media ? media.querySelector('[data-ugc-flash]') : null;
    if (!flash) return;
    const mutedIcon = flash.querySelector('[data-flash-icon="muted"]');
    const onIcon = flash.querySelector('[data-flash-icon="on"]');
    if (mutedIcon) mutedIcon.toggleAttribute('hidden', soundOn);
    if (onIcon) onIcon.toggleAttribute('hidden', !soundOn);
    flash.classList.remove('is-flashing');
    void flash.offsetWidth;
    flash.classList.add('is-flashing');
  }

  updatePlayButton(video) {
    const controls = this.controlsFor(video);
    if (!controls || !controls.playPause) return;
    const paused = video.paused;
    controls.playPause.setAttribute(
      'aria-label',
      paused ? controls.playPause.dataset.labelPlay : controls.playPause.dataset.labelPause
    );
    // SVG elements lack the HTML hidden property; toggle the attribute.
    const playIcon = controls.playPause.querySelector('[data-pp-icon="play"]');
    const pauseIcon = controls.playPause.querySelector('[data-pp-icon="pause"]');
    if (playIcon) playIcon.toggleAttribute('hidden', !paused);
    if (pauseIcon) pauseIcon.toggleAttribute('hidden', paused);
  }

  /* Exactly one video carries sound; switching updates every sound button. */
  setAudible(video) {
    this.audible = video;
    this.videos.forEach((other) => {
      other.muted = other !== video;
    });
    this.videos.forEach((other) => {
      const controls = this.controlsFor(other);
      if (!controls || !controls.sound) return;
      const on = other === video && video !== null;
      controls.sound.setAttribute('aria-pressed', on ? 'true' : 'false');
      controls.sound.setAttribute('aria-label', on ? controls.sound.dataset.labelOff : controls.sound.dataset.labelOn);
      const mutedIcon = controls.sound.querySelector('[data-sound-icon="muted"]');
      const onIcon = controls.sound.querySelector('[data-sound-icon="on"]');
      if (mutedIcon) mutedIcon.toggleAttribute('hidden', on);
      if (onIcon) onIcon.toggleAttribute('hidden', !on);
    });
  }

  /* One authority over play state: user-paused videos never play, user
     intent and sound outrank the cap, then on-screen videos in order. */
  syncPlayback() {
    const cap = this.reducedMotion.matches ? 0 : this.maxPlaying;
    let playing = 0;
    const ordered = this.videos
      .filter((video) => this.inView.has(video))
      .sort(
        (a, b) =>
          (b === this.audible || this.userStarted.has(b) ? 1 : 0) -
          (a === this.audible || this.userStarted.has(a) ? 1 : 0)
      );

    ordered.forEach((video) => {
      const elevated = video === this.audible || this.userStarted.has(video);
      const shouldPlay =
        !this.userPaused.has(video) &&
        !this.reducedMotion.matches &&
        (elevated || playing < cap);
      if (shouldPlay) {
        playing += 1;
        const played = video.play();
        if (played && played.catch) played.catch(() => {});
      } else {
        video.pause();
      }
    });
    this.videos.forEach((video) => {
      if (!this.inView.has(video)) video.pause();
    });
  }

  step(delta) {
    const total = this.cards.length;
    this.index = (this.index + delta + total) % total;
    this.update();
  }

  update() {
    const total = this.cards.length;
    const count = Math.min(this.visibleCount, total);
    this.cards.forEach((card) => {
      card.classList.remove('is-visible');
      card.style.order = '';
    });
    for (let offset = 0; offset < count; offset += 1) {
      const card = this.cards[(this.index + offset) % total];
      card.classList.add('is-visible');
      card.style.order = String(offset + 1);
    }
    /* From here the CSS follows is-visible rather than standing in with the
       first card; see section-ugc-reviews.css. */
    this.setAttribute('data-ready', '');
  }
}

if (!customElements.get('wildgainz-ugc-reviews')) {
  customElements.define('wildgainz-ugc-reviews', WildgainzUgcReviews);
}
})();
